import React, { useState, useEffect, useCallback } from 'react'
import { XMarkIcon, DocumentTextIcon, ExclamationTriangleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline'
import axiosInstance from '../../api/axiosInstance'
import LoadingSpinner from '../LoadingSpinner'
import toast from 'react-hot-toast'

interface MercadoLibreShippingLabelModalProps {
  isOpen: boolean
  onClose: () => void
  ordersId: string | number
  orderNumber?: string
}

interface LabelData {
  pdfUrl: string
  blob: Blob
  shipmentId: string
  shipmentIds: string[]
  zplContent?: string
  shipmentInfo?: {
    shipment_id: number
    label: string
  }
}

const MercadoLibreShippingLabelModal: React.FC<MercadoLibreShippingLabelModalProps> = ({
  isOpen,
  onClose,
  ordersId,
  orderNumber
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [labelData, setLabelData] = useState<LabelData | null>(null)
  const [hasSuccessfulLoad, setHasSuccessfulLoad] = useState(false)

  const handleGetShippingLabel = useCallback(async () => {
    const ordersIdStr = String(ordersId || '').trim()
    if (!ordersIdStr || ordersIdStr === '0' || ordersIdStr === 'undefined') {
      toast.error('ID de pedido es requerido')
      return
    }

    setIsLoading(true)
    setError(null)
    setLabelData(null)

    try {
      const token = localStorage.getItem('accessToken')
      if (!token) {
        toast.error('Error de autenticación')
        return
      }

      // Llamar al endpoint de MercadoLibre para obtener etiquetas de envío
      const response = await axiosInstance.get(`/mercadolibre/shipment-labels?shipment_ids=${ordersIdStr}&response_type=json`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.data && response.data.success && response.data.data && Array.isArray(response.data.data)) {
        // La API devuelve un array con información de etiquetas
        const labelData = response.data.data[0] // Tomar la primera etiqueta
        
        console.log('📦 Respuesta completa de la API:', response.data)
        console.log('🏷️ Datos de etiqueta:', labelData)
        
        if (!labelData || !labelData.label) {
          throw new Error('No se encontró información de etiqueta en la respuesta')
        }
        
        try {
          // La etiqueta viene en formato ZPL (Zebra Programming Language)
          const zplContent = labelData.label
          console.log('📄 Contenido ZPL (primeros 200 caracteres):', zplContent.substring(0, 200))
          
          // Convertir el contenido ZPL a bytes para crear un archivo
          const bytes = new Uint8Array(zplContent.length)
          for (let i = 0; i < zplContent.length; i++) {
            bytes[i] = zplContent.charCodeAt(i) & 0xFF
          }
          
          // Crear blob con el contenido ZPL
          const blob = new Blob([bytes as any], { type: 'text/plain' })
          console.log('📁 Blob creado:', blob.size, 'bytes')
          
          // Validar que el blob tenga contenido
          if (blob.size === 0) {
            throw new Error('El archivo generado está vacío')
          }
          
          const url = window.URL.createObjectURL(blob)
          console.log('🔗 URL creada:', url)
          
          setLabelData({
            pdfUrl: url,
            blob: blob,
            shipmentId: ordersIdStr,
            shipmentIds: response.data.shipment_ids || [ordersIdStr],
            zplContent: zplContent,
            shipmentInfo: labelData
          })
          setHasSuccessfulLoad(true)
          
          if (!isInitialLoad) {
            toast.success('Etiqueta de envío obtenida exitosamente')
          }
        } catch (processError) {
          console.error('❌ Error procesando etiqueta:', processError)
          console.error('📦 Datos que causaron el error:', {
            labelData: labelData,
            type: typeof labelData
          })
          const errorMessage = processError instanceof Error ? processError.message : 'Error desconocido al procesar la etiqueta'
          setError(`Error al procesar la etiqueta: ${errorMessage}`)
          setHasSuccessfulLoad(false)
          if (!isInitialLoad) {
            toast.error(`Error al procesar la etiqueta: ${errorMessage}`)
          }
        }
      } else if (response.data && !response.data.success) {
        // La API devolvió un error
        setError(response.data.message || 'Error al obtener la etiqueta de envío')
        setHasSuccessfulLoad(false)
        if (!isInitialLoad) {
          toast.error(response.data.message || 'Error al obtener la etiqueta')
        }
      } else {
        setError('No se pudo obtener la etiqueta de envío')
        setHasSuccessfulLoad(false)
        if (!isInitialLoad) {
          toast.error('Error al obtener la etiqueta')
        }
      }
    } catch (err: any) {
      console.error('Error al obtener etiqueta de MercadoLibre:', err)
      
      let errorMessage = 'Error al consultar la etiqueta de envío'
      
      if (err.response?.status === 404) {
        errorMessage = 'No se encontró información de envío para este pedido'
      } else if (err.response?.status === 400) {
        errorMessage = 'ID de envío inválido o pedido no tiene envío asociado'
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message
      }
      
      setError(errorMessage)
      setHasSuccessfulLoad(false)
      if (!isInitialLoad) {
        toast.error(errorMessage)
      }
    } finally {
      setIsLoading(false)
      setIsInitialLoad(false)
    }
  }, [ordersId, isInitialLoad])

  // Consultar automáticamente al abrir el modal
  useEffect(() => {
    const ordersIdStr = String(ordersId || '').trim()
    if (isOpen && ordersIdStr && ordersIdStr !== '0' && ordersIdStr !== 'undefined') {
      handleGetShippingLabel()
    }
  }, [isOpen, ordersId, handleGetShippingLabel])

  const handleClose = () => {
    // Limpiar URL del blob si existe
    if (labelData?.pdfUrl) {
      window.URL.revokeObjectURL(labelData.pdfUrl)
    }
    setLabelData(null)
    setError(null)
    setIsInitialLoad(true)
    setHasSuccessfulLoad(false)
    onClose()
  }

  const downloadPDF = async () => {
    if (!labelData?.zplContent) {
      toast.error('No hay contenido ZPL disponible para convertir a PDF')
      return
    }

    try {
      setIsLoading(true)
      
      // Usar un servicio online para convertir ZPL a PDF
      const response = await fetch('https://api.labelary.com/v1/printers/8dpmm/labels/4x6/0/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: labelData.zplContent
      })

      if (!response.ok) {
        throw new Error('Error al convertir ZPL a PDF')
      }

      const pdfBlob = await response.blob()
      const pdfUrl = window.URL.createObjectURL(pdfBlob)

      // Crear elemento de descarga
      const link = document.createElement('a')
      link.href = pdfUrl
      link.download = `etiqueta-envio-mercadolibre-${String(ordersId || '')}.pdf`
      link.style.display = 'none'
      
      // Simular click para descargar
      document.body.appendChild(link)
      link.click()
      
      // Limpiar después de un breve delay
      setTimeout(() => {
        document.body.removeChild(link)
        window.URL.revokeObjectURL(pdfUrl)
      }, 100)
      
      toast.success('Etiqueta de envío PDF descargada exitosamente')
    } catch (err) {
      console.error('Error convirtiendo ZPL a PDF:', err)
      toast.error('Error al convertir a PDF. Descargando ZPL como alternativa.')
      
      // Fallback: descargar ZPL si falla la conversión
      if (labelData?.pdfUrl) {
        const link = document.createElement('a')
        link.href = labelData.pdfUrl
        link.download = `etiqueta-envio-mercadolibre-${String(ordersId || '')}.zpl`
        link.style.display = 'none'
        
        document.body.appendChild(link)
        link.click()
        
        setTimeout(() => {
          document.body.removeChild(link)
        }, 100)
      }
    } finally {
      setIsLoading(false)
    }
  }

  const openPDFInNewTab = async () => {
    if (!labelData?.zplContent) {
      toast.error('No hay contenido ZPL disponible para convertir a PDF')
      return
    }

    try {
      setIsLoading(true)
      
      // Usar un servicio online para convertir ZPL a PDF
      const response = await fetch('https://api.labelary.com/v1/printers/8dpmm/labels/4x6/0/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: labelData.zplContent
      })

      if (!response.ok) {
        throw new Error('Error al convertir ZPL a PDF')
      }

      const pdfBlob = await response.blob()
      const pdfUrl = window.URL.createObjectURL(pdfBlob)

      // Abrir PDF en nueva pestaña
      window.open(pdfUrl, '_blank')
      
      // Limpiar URL después de un tiempo
      setTimeout(() => {
        window.URL.revokeObjectURL(pdfUrl)
      }, 10000)
      
    } catch (err) {
      console.error('Error convirtiendo ZPL a PDF:', err)
      toast.error('Error al convertir a PDF. Abriendo ZPL como alternativa.')
      
      // Fallback: abrir ZPL si falla la conversión
      if (labelData?.pdfUrl) {
        window.open(labelData.pdfUrl, '_blank')
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <DocumentTextIcon className="h-6 w-6 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">
              Etiqueta de Envío MercadoLibre
            </h3>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Order Info */}
          <div className="mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm font-medium text-blue-800">Pedido:</span>
                <span className="text-sm text-blue-600 font-mono">
                  {orderNumber || String(ordersId || 'N/A')}
                </span>
              </div>
              <div className="text-xs text-blue-600">
                ID de envío: {ordersId || 'N/A'}
              </div>
            </div>
          </div>

          {/* Loading State */}
          {isLoading && (
            <div className="mb-6">
              <div className="flex items-center justify-center py-8">
                <div className="text-center">
                  <LoadingSpinner size="large" color="text-blue-600" />
                  <p className="text-sm text-gray-600 mt-2">
                    Generando etiqueta de envío...
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Action Button - Solo mostrar si no está cargando y no hubo carga exitosa */}
          {!isLoading && !hasSuccessfulLoad && (
            <div className="mb-6">
              {String(ordersId || '').trim() && String(ordersId || '').trim() !== '0' && String(ordersId || '').trim() !== 'undefined' ? (
                <button
                  onClick={handleGetShippingLabel}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                >
                  <DocumentTextIcon className="h-4 w-4" />
                  Generar Etiqueta Nuevamente
                </button>
              ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />
                    <span className="text-sm font-medium text-yellow-800">ID de Pedido No Válido</span>
                  </div>
                  <p className="text-sm text-yellow-600">
                    No se puede generar la etiqueta sin un ID de pedido válido.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Results */}
          {error && (
            <div className="mb-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />
                  <span className="text-sm font-medium text-red-800">Error</span>
                </div>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}

          {labelData && (
            <div className="mb-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <DocumentTextIcon className="h-5 w-5 text-blue-500" />
                  <span className="text-sm font-medium text-blue-800">
                    Etiqueta de Envío Generada
                  </span>
                </div>
                
                {/* Información del Shipment */}
                {labelData.shipmentInfo && (
                  <div className="mb-3 p-3 bg-white rounded border">
                {/*     <div className="text-xs text-gray-600 mb-1">Información del Envío:</div>
                    <div className="text-sm font-mono text-gray-800">
                      Shipment ID: {labelData.shipmentInfo.shipment_id}
                    </div> */}
                  </div>
                )}
                
                <div className="space-y-3">
{/*                   <div>
                    <span className="text-xs font-medium text-blue-700 block">ID de Envío:</span>
                    <span className="text-sm text-blue-800 font-mono">
                      {labelData.shipmentId}
                    </span>
                  </div> */}
                  
                  <div>
                    <span className="text-xs font-medium text-blue-700 block">Formato:</span>
                    <span className="text-sm text-blue-800">
                      ZPL → PDF (Conversión automática)
                    </span>
                  </div>

                  {/* Botones de acción */}
                  <div className="pt-3 border-t border-blue-200 space-y-2">
                    <button
                      onClick={openPDFInNewTab}
                      disabled={isLoading}
                      className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <DocumentTextIcon className="h-4 w-4" />
                      {isLoading ? 'Convirtiendo...' : 'Ver PDF'}
                    </button>
                    
                    <button
                      onClick={downloadPDF}
                      disabled={isLoading}
                      className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                      {isLoading ? 'Convirtiendo...' : 'Descargar PDF'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  )
}

export default MercadoLibreShippingLabelModal
